import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import './Auth.css';

const Setup2FA = () => {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const { setup2FA, verify2FA } = useAuth();

  useEffect(() => {
    const initiate2FA = async () => {
      const result = await setup2FA();
      if (result.success) {
        setQrCode(result.data.qrCode);
        setSecret(result.data.secret);
        setLoading(false);
      } else {
        setError(result.error);
        setLoading(false);
      }
    };

    initiate2FA();
  }, []);

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto focus next input
    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`).focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-${index - 1}`).focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter a 6-digit code');
      setVerifying(false);
      return;
    }

    const result = await verify2FA(fullCode);
    setVerifying(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Verification failed');
      setCode(['', '', '', '', '', '']);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="spinner">
            <div className="loading"></div>
            <span>Initializing 2FA...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Set Up Two-Factor Authentication</h1>
        <p className="auth-subtitle">Secure your account with 2FA</p>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="qr-code-container">
          <p>Scan this QR code with your authenticator app:</p>
          {qrCode && <QRCode value={qrCode} size={200} />}
          <p style={{ fontSize: '12px', marginTop: '8px' }}>
            Or enter this code manually: <code>{secret}</code>
          </p>
        </div>

        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label className="form-label">Enter 6-digit code</label>
            <div className="code-input-container">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  maxLength="1"
                  className="code-input"
                  value={code[index]}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  disabled={verifying}
                />
              ))}
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={verifying}
          >
            {verifying ? 'Verifying...' : 'Verify & Continue'}
          </button>
        </form>

        <div className="alert alert-info" style={{ marginTop: '16px' }}>
          <strong>Save your backup codes</strong>
          <p style={{ fontSize: '12px', marginTop: '4px' }}>
            Store your backup codes in a safe place. You can use them to regain access if you lose your authenticator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Setup2FA;
